import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";
import { makeOfflineRequest } from "../../shared/utils.js/offlineRequestManager";

// Departments async thunks
export const getDepartments = createAsyncThunk(
  "Departments/getDepartments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      const url = `/departments${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
      const response = await makeOfflineRequest(url, "GET");
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to get departments!";
      return rejectWithValue(message);
    }
  },
);

export const addDepartment = createAsyncThunk(
  "Departments/addDepartment",
  async (departmentData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/departments`,
        departmentData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add department!";
      return rejectWithValue(message);
    }
  },
);

export const updateDepartment = createAsyncThunk(
  "Departments/updateDepartment",
  async ({ id, departmentData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.put(
        `${BASE_URL}/departments/${id}`,
        departmentData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update department!";
      return rejectWithValue(message);
    }
  },
);

export const deleteDepartment = createAsyncThunk(
  "Departments/deleteDepartment",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/departments/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete department!";
      return rejectWithValue(message);
    }
  },
);

export const getDepartmentDetails = createAsyncThunk(
  "Departments/getDepartmentDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/departments/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get department details!";
      return rejectWithValue(message);
    }
  },
);
